import {
  SearchableKeyword,
  SearchableViewItems,
  SearchableViewItemsGroup,
} from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import { store } from '@nirvana/redux/store';
import { setInitialTreeState } from '../../../redux/actions';
import { ModuleId, SectionId, TreePath } from '@nirvana/shared/dtos/types/policies/common';
import { PolicySearchableKeywords } from '../../../utils';
import { getDefaultModulesState } from '../../../redux/slice';
import { PolicyContext } from '../../../redux/navigationHeaderSlice/types.navigation';
import _ from 'lodash';
import { SectionMask } from '../../../redux/types';
import { PolicyPanelManager } from '../../../PolicyPanelManager';
import { getModulesGuards, getTreeConfig, SectionConfig } from '../../../utils/configs';

export function getLabelForPolicyContext(context: PolicyContext): string {
  switch (context) {
    case PolicyContext.ADD:
      return translateFn('Add');
    case PolicyContext.EDIT:
      return translateFn('Edit');
    case PolicyContext.CLONE:
      return translateFn('Clone');
    default:
      return '';
  }
}

export function getModuleLabelById(moduleId: ModuleId): string {
  switch (moduleId) {
    case ModuleId.POLICY_SETTINGS:
      return translateFn('Policy');
    case ModuleId.GENERAL:
      return translateFn('Agent');
    case ModuleId.PATCH_MANAGEMENT:
      return translateFn('Patch Management');
    case ModuleId.INTEGRITY_MONITOR:
      return translateFn('Integrity Monitoring');
    case ModuleId.EDR_SENSOR:
      return translateFn('Incidents Sensor');
    case ModuleId.ENCRYPTION:
      return translateFn('Encryption');
    case ModuleId.STORAGE_PROTECTION:
      return translateFn('Storage Protection');
    case ModuleId.INDICATORS_OF_RISK:
      return translateFn('Risk Management');
    case ModuleId.DEVICE_CONTROL:
      return translateFn('Device Control');
    case ModuleId.RELAY:
      return translateFn('Relay');
    case ModuleId.LIVE_SEARCH:
      return translateFn('Live Search');
    case ModuleId.SANDBOX:
      return translateFn('Sandbox Analyzer');
    case ModuleId.APPLICATION_CONTROL:
      return translateFn('Application Control');
    case ModuleId.NSX:
      return translateFn('NSX');
    case ModuleId.FIREWALL:
      return translateFn('Firewall');
    case ModuleId.CONTENT_CONTROL:
      return translateFn('Network Protection');
    case ModuleId.EXCHANGE_PROTECTION:
      return translateFn('Exchange Protection');
    case ModuleId.ANTIMALWARE:
      return translateFn('Antimalware');
    case ModuleId.BLOCKLIST_RULES:
      return translateFn('Blocklist');
    default:
      return '';
  }
}

export function getSectionLabelById(sectionId: SectionId, moduleId?: ModuleId): string {
  switch (sectionId) {
    case SectionId.INTEGRITY_MONITOR_REAL_TIME:
      return translateFn('Real Time');
    case SectionId.GENERAL:
    case SectionId.CONTENT_CONTROL_TRAFFIC:
      return translateFn('General');
    case SectionId.SETTINGS:
      switch (moduleId) {
        case ModuleId.FIREWALL:
          return translateFn('General');
        default:
          return translateFn('Settings');
      }
    case SectionId.STORAGE_PROTECTION_ICAP:
      return translateFn('General');
    case SectionId.EXCLUSIONS:
      return translateFn('Exclusions');
    case SectionId.RULES:
      return translateFn('Rules');
    case SectionId.COMMUNICATION:
      return translateFn('Communication');
    case SectionId.UPDATE:
      return translateFn('Update');
    case SectionId.ENDPOINT_SENSOR:
      return translateFn('Endpoint Sensor');
    case SectionId.NETWORK_SENSOR:
      return translateFn('Network Sensor');
    case SectionId.ICAP_SENSOR:
      return translateFn('ICAP Sensor');
    case SectionId.SANDBOX_MANAGER:
      return translateFn('Sandbox Manager');
    case SectionId.FIREWALL_NETWORK:
      return translateFn('Settings');
    case SectionId.FIREWALL_ADVANCED:
      return translateFn('Rules');
    case SectionId.CONTENT_CONTROL_DATA_PROTECTION:
      return translateFn('Web Protection');
    case SectionId.CONTENT_CONTROL_NETWORK_MONITOR:
      return translateFn('Network Attacks');
    case SectionId.CONTENT_CONTROL_WEB:
    case SectionId.EXCHANGE_CONTENT_CONTROL:
      return translateFn('Content Control');
    case SectionId.EXCHANGE_ANTIMALWARE:
      return translateFn('Antimalware');
    case SectionId.EXCHANGE_ANTISPAM:
      return translateFn('Antispam');
    case SectionId.ANTIMALWARE_ANTI_TAMPERING:
      return translateFn('Anti-Tampering');
    case SectionId.ANTIMALWARE_ON_ACCESS:
      return translateFn('On-Access');
    case SectionId.ANTIMALWARE_ON_EXECUTE:
      return translateFn('On-Execute');
    case SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE:
      return translateFn('Hyper Detect');
    case SectionId.ANTIMALWARE_ANTI_EXPLOIT:
      return translateFn('Advanced Anti-Exploit');
    case SectionId.ANTIMALWARE_SECURITY_SERVERS:
      return translateFn('Security Servers');
    case SectionId.ANTIMALWARE_ON_DEMAND:
      return translateFn('On-Demand');
    case SectionId.SECURITY_TELEMETRY:
      return translateFn('Security Telemetry');
    case SectionId.DETAILS:
      return translateFn('Details');
    case SectionId.GENERAL_NOTIFICATIONS:
      return translateFn('Notifications');
    case SectionId.GENERAL_INHERITANCE:
      return translateFn('Inheritance rules');
    case SectionId.ALL:
      return translateFn('All');
    default:
      return '';
  }
}

export function getWarningMessageByMask(path: TreePath, mask: SectionMask): string {
  switch (mask) {
    case SectionMask.DEFAULT:
      return translateFn('This is the default policy and cannot be modified.');
    case SectionMask.INHERITANCE:
      return translateFn(
        'Settings in this section are inherited from {bold}{policyName}{/bold}. To modify them, edit the original policy.',
        {
          policyName: PolicyPanelManager.getInstance().getInheritancePolicyNameForPath(path),
          bold: '<strong>',
          '/bold': '</strong>',
        }
      );
    case SectionMask.LICENSE_RESTRICTION:
      return translateFn(
        'Editing this section requires another type of license. For licensing details, contact your Bitdefender representative.'
      );
    case SectionMask.NETWORK_PROTECTION_TRAFFIC:
      return translateFn('This section requires Network Protection to be enabled in this policy.');
    case SectionMask.ANTIMALWARE_ON_ACCESS:
      if (path.moduleId === ModuleId.ANTIMALWARE && path.sectionId === SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE) {
        return translateFn('Hyper Detect is unavailable because the On-access Scanning module is disabled.');
      }

      return '';
    default:
      return '';
  }
}

function convertObjectWithSearchableKeywordsToArray(
  getObjectWithSearchableKeywords: Function
): Array<SearchableKeyword> {
  const objectWithSearchableKeywords = getObjectWithSearchableKeywords();
  const searchableKeywords: Array<SearchableKeyword> = [];

  // compute array of searchable keywords from object
  Object.entries(objectWithSearchableKeywords).forEach(
    ([keywords, labels]: [PolicySearchableKeywords, SearchableKeyword]) => searchableKeywords.push(labels)
  );
  return searchableKeywords;
}

// The ui element id must be unique. We use the module id and section id to create a unique id for section.
// Example: settings, exclusions are used in multiple modules.
function convertSectionIdToUiElementId(moduleId: ModuleId, sectionId: SectionId): string {
  return moduleId + '-' + sectionId;
}

// The ui element id must be unique. Use the last part of the ui element id to get the section id.
export function convertUiElementIdToSectionId(uiElementName: string): SectionId {
  return uiElementName.split('-').at(-1) as unknown as SectionId;
}

export function getPolicyPanelTreeItems(phantom: boolean): SearchableViewItems[] {
  // default module state
  const defaultModulesState = getDefaultModulesState();
  const treeConfig = getTreeConfig();
  const modulesGuards = getModulesGuards();

  // tree state
  const treeItemsArray: SearchableViewItems[] = [];
  const initialTreeState = {};
  const groups = {
    general: {
      id: 'general-group',
      label: translateFn('General'),
    },
    protection: {
      id: 'protection-group',
      label: translateFn('Protection & monitoring'),
    },
  };

  // method to set search view item for a module
  const setSearchableViewItemFn = (moduleId: ModuleId, group: SearchableViewItemsGroup) => {
    // skip the module if the guard is not true
    if (modulesGuards[moduleId] && !modulesGuards[moduleId]()) {
      return;
    }

    // set the initial state for the module
    initialTreeState[moduleId] = defaultModulesState[moduleId];

    // create the module item
    const item: SearchableViewItems = {
      id: moduleId,
      key: moduleId,
      label: getModuleLabelById(moduleId),
      children: [],
      group: group,
    };

    if (treeConfig[moduleId][SectionId.NONE]) {
      // add the section to the module item
      const sectionRoute = treeConfig[moduleId][SectionId.NONE];
      item.route = sectionRoute.route;
      item.uiElementName = sectionRoute.uiElementName;
      item.searchableKeywords = convertObjectWithSearchableKeywordsToArray(sectionRoute.searchableKeywords);
    } else {
      // iterate over the sections
      Object.entries(treeConfig[moduleId]).forEach(([sectionId, sectionConfig]: [SectionId, SectionConfig]) => {
        if (sectionConfig.guard()) {
          // add children only if the section is available
          item.children.push({
            id: convertSectionIdToUiElementId(moduleId, sectionId),
            label: getSectionLabelById(sectionId, moduleId),
            uiElementName: sectionConfig.uiElementName,
            searchableKeywords: convertObjectWithSearchableKeywordsToArray(sectionConfig.searchableKeywords),
          });
        } else {
          // remove the section from the initial state
          _.unset(initialTreeState[moduleId]?.sections, [sectionId]);
        }
      });
    }

    // add the module item to the tree
    treeItemsArray.push(item);
  };

  setSearchableViewItemFn(ModuleId.POLICY_SETTINGS, groups.general);
  setSearchableViewItemFn(ModuleId.GENERAL, groups.general);
  setSearchableViewItemFn(ModuleId.RELAY, groups.general);
  setSearchableViewItemFn(ModuleId.ANTIMALWARE, groups.protection);
  setSearchableViewItemFn(ModuleId.SANDBOX, groups.protection);
  setSearchableViewItemFn(ModuleId.FIREWALL, groups.protection);
  setSearchableViewItemFn(ModuleId.CONTENT_CONTROL, groups.protection);
  setSearchableViewItemFn(ModuleId.PATCH_MANAGEMENT, groups.protection);
  setSearchableViewItemFn(ModuleId.APPLICATION_CONTROL, groups.protection);
  setSearchableViewItemFn(ModuleId.DEVICE_CONTROL, groups.protection);
  setSearchableViewItemFn(ModuleId.INTEGRITY_MONITOR, groups.protection);
  setSearchableViewItemFn(ModuleId.EXCHANGE_PROTECTION, groups.protection);
  setSearchableViewItemFn(ModuleId.ENCRYPTION, groups.protection);
  setSearchableViewItemFn(ModuleId.EDR_SENSOR, groups.protection);
  setSearchableViewItemFn(ModuleId.STORAGE_PROTECTION, groups.protection);
  setSearchableViewItemFn(ModuleId.INDICATORS_OF_RISK, groups.protection);
  setSearchableViewItemFn(ModuleId.BLOCKLIST_RULES, groups.protection);
  setSearchableViewItemFn(ModuleId.LIVE_SEARCH, groups.protection);
  setSearchableViewItemFn(ModuleId.NSX, groups.protection);

  // phantom will be changed depending on the user action (add/edit policy)
  store.dispatch(setInitialTreeState({ modules: initialTreeState, phantom: phantom }));

  return treeItemsArray;
}
